import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, MenuItem, TextField,
  Button, CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Stack, Alert, Paper, LinearProgress, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, Tab, Tabs, InputAdornment, Switch, FormControlLabel
} from '@mui/material';
import {
  AccountTree, Add, Edit, Delete, Sync, SyncAlt,
  School, People, AttachMoney, EventNote, BarChart,
  CheckCircle, Warning, Error as ErrorIcon, Settings,
  Visibility, VisibilityOff, Cable, History, Person, TrendingUp,
  FileDownload, Refresh
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { exportCSV } from '../utils/export';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api' });
api.interceptors.request.use(c => {
  const t = localStorage.getItem('sms_token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

const fmt  = n => `E${parseFloat(n || 0).toLocaleString('en', { minimumFractionDigits: 2 })}`;
const fmtN = n => parseInt(n || 0).toLocaleString();
const pct  = n => `${parseFloat(n || 0).toFixed(1)}%`;
const fmtD = d => d ? new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const SYNC_COLOR   = { ok: 'success', error: 'error', partial: 'warning' };
const SCHOOL_TYPES = ['primary','secondary','combined','technical','special'];
const ptLabel = v => (v || '').replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase());

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

// ── Network Group Dashboard ────────────────────────────────────────────────────
function GroupDashboard() {
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData]         = useState(null);
  const [networks, setNetworks] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [syncing, setSyncing]   = useState(false);
  const [network, setNetwork]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dr, nr] = await Promise.all([
        api.get('/network/dashboard', { params: { network_id: network || undefined } }),
        api.get('/network/networks')
      ]);
      if (dr.data.success) setData(dr.data.data);
      if (nr.data.success) setNetworks(nr.data.data);
    } catch { enqueueSnackbar('Failed to load dashboard', { variant: 'error' }); }
    finally { setLoading(false); }
  }, [network, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const syncAll = async () => {
    setSyncing(true);
    try {
      const r = await api.post('/network/sync-all', null, { params: { network_id: network || undefined } });
      const ok = r.data.results?.filter(x => x.status === 'ok').length || 0;
      const err = r.data.results?.filter(x => x.status === 'error').length || 0;
      enqueueSnackbar(`Sync complete: ${ok} succeeded, ${err} failed`, { variant: ok > 0 ? 'success' : 'warning' });
      load();
    } catch { enqueueSnackbar('Sync failed', { variant: 'error' }); }
    finally { setSyncing(false); }
  };

  const syncOne = async (schoolId, schoolName) => {
    try {
      const r = await api.post(`/network/schools/${schoolId}/sync`);
      if (r.data.success) { enqueueSnackbar(`${schoolName} synced`, { variant: 'success' }); load(); }
      else enqueueSnackbar(`${schoolName}: ${r.data.message}`, { variant: 'error' });
    } catch { enqueueSnackbar('Sync failed', { variant: 'error' }); }
  };

  const t = data?.totals;

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center" flexWrap="wrap">
        <TextField select size="small" label="Network" value={network} onChange={e => setNetwork(e.target.value)} sx={{ minWidth: 200 }}>
          <MenuItem value="">All Networks</MenuItem>
          {networks.map(n => <MenuItem key={n.id} value={n.id}>{n.name}</MenuItem>)}
        </TextField>
        <Box sx={{ flexGrow: 1 }} />
        {data && (
          <Typography variant="caption" color="text.secondary">
            {data.synced_count}/{data.total_schools} schools synced
          </Typography>
        )}
        <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={load}>Refresh</Button>
        <Button variant="contained" size="small" startIcon={syncing ? <CircularProgress size={14} color="inherit" /> : <SyncAlt />}
          onClick={syncAll} disabled={syncing} sx={{ bgcolor: '#0f2b4a' }}>
          {syncing ? 'Syncing…' : 'Sync All Schools'}
        </Button>
      </Stack>

      {loading ? <CircularProgress /> : t && (
        <>
          {/* Group KPI cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Total Enrolled',    value: fmtN(t.enrolled),       sub: `${fmtN(t.male)}M / ${fmtN(t.female)}F`, color: '#0f2b4a' },
              { label: 'Total Teachers',    value: fmtN(t.teachers),       sub: `${t.ratio}:1 ratio`,                     color: '#2e7d32' },
              { label: 'Avg Attendance',    value: pct(t.avg_attendance),  sub: 'across network',                         color: t.avg_attendance >= 85 ? '#2e7d32' : '#e65100' },
              { label: 'Avg Academic Score',value: pct(t.avg_academic),    sub: 'across subjects',                        color: t.avg_academic >= 60 ? '#2e7d32' : '#e65100' },
              { label: 'Total Billed',      value: fmt(t.billed),          sub: `${pct(t.avg_fee_rate)} collected`,        color: '#c9952a' },
              { label: 'Outstanding Fees',  value: fmt(t.outstanding),     sub: 'across all schools',                     color: '#c62828' },
              { label: 'Total Boarders',    value: fmtN(t.boarders),       sub: 'across all schools',                     color: '#1565c0' },
              { label: 'Bursary Recipients',value: fmtN(t.bursaries),      sub: 'active awards',                          color: '#6a1b9a' },
            ].map(card => (
              <Grid item xs={6} sm={3} key={card.label}>
                <Card variant="outlined" sx={{ borderRadius: 2, borderLeft: `4px solid ${card.color}` }}>
                  <CardContent sx={{ pb: '12px !important' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" fontSize={9}>{card.label}</Typography>
                    <Typography variant="h5" fontWeight={800} sx={{ color: card.color, mt: 0.3, fontSize: 20 }}>{card.value}</Typography>
                    <Typography fontSize={10} color="text.secondary">{card.sub}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Per-school breakdown cards */}
          <Typography fontWeight={700} sx={{ mb: 1.5 }}>School-by-School Overview</Typography>
          <Grid container spacing={2}>
            {data.schools?.map(school => {
              const synced = school.sync_status === 'ok' && school.total_enrolled != null;
              return (
                <Grid item xs={12} sm={6} md={4} key={school.id}>
                  <Card variant="outlined" sx={{ borderRadius: 2,
                    border: school.sync_status === 'error' ? '1px solid #fca5a5' :
                            !school.sync_status ? '1px solid #e0e0e0' : undefined,
                    height: '100%' }}>
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                        <Box sx={{ flexGrow: 1, mr: 1 }}>
                          <Typography fontWeight={700} fontSize={14}>{school.name}</Typography>
                          <Stack direction="row" spacing={0.5} sx={{ mt: 0.3 }}>
                            <Chip label={school.code} size="small" sx={{ fontSize: 10, fontFamily: 'monospace' }} />
                            <Chip label={ptLabel(school.school_type)} size="small" color="primary" sx={{ fontSize: 10 }} />
                            {school.region && <Chip label={school.region} size="small" sx={{ fontSize: 10 }} />}
                          </Stack>
                        </Box>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Chip label={school.sync_status || 'Never'} size="small"
                            color={SYNC_COLOR[school.sync_status] || 'default'} sx={{ fontSize: 9 }} />
                          <Tooltip title="Sync this school">
                            <IconButton size="small" onClick={() => syncOne(school.id, school.name)}>
                              <Sync sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>

                      {school.sync_status === 'error' ? (
                        <Alert severity="error" sx={{ fontSize: 11, py: 0.5 }}>{school.sync_error || 'Connection failed'}</Alert>
                      ) : !synced ? (
                        <Typography fontSize={12} color="text.secondary" sx={{ py: 1 }}>
                          Not yet synced. Click the sync button to pull live data.
                        </Typography>
                      ) : (
                        <>
                          <Grid container spacing={1} sx={{ mb: 1 }}>
                            <Grid item xs={4}>
                              <Typography fontSize={9} color="text.secondary" textTransform="uppercase">Enrolled</Typography>
                              <Typography fontWeight={700} fontSize={16}>{fmtN(school.total_enrolled)}</Typography>
                              <Typography fontSize={10} color="text.secondary">{school.male_enrolled}M/{school.female_enrolled}F</Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography fontSize={9} color="text.secondary" textTransform="uppercase">Teachers</Typography>
                              <Typography fontWeight={700} fontSize={16}>{fmtN(school.total_teachers)}</Typography>
                              <Typography fontSize={10} color="text.secondary">{school.student_teacher_ratio}:1</Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography fontSize={9} color="text.secondary" textTransform="uppercase">Avg Score</Typography>
                              <Typography fontWeight={700} fontSize={16} color={school.avg_academic_score >= 60 ? 'success.main' : 'error.main'}>{pct(school.avg_academic_score)}</Typography>
                            </Grid>
                          </Grid>

                          <Box sx={{ mb: 0.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                              <Typography fontSize={10} color="text.secondary">Attendance</Typography>
                              <Typography fontSize={10} fontWeight={700} color={school.attendance_rate >= 85 ? 'success.main' : 'error.main'}>{pct(school.attendance_rate)}</Typography>
                            </Box>
                            <LinearProgress variant="determinate" value={Math.min(parseFloat(school.attendance_rate) || 0, 100)}
                              color={school.attendance_rate >= 85 ? 'success' : school.attendance_rate >= 70 ? 'warning' : 'error'}
                              sx={{ height: 5, borderRadius: 3, mb: 0.5 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                              <Typography fontSize={10} color="text.secondary">Fee Collection</Typography>
                              <Typography fontSize={10} fontWeight={700} color={school.fee_collection_rate >= 80 ? 'success.main' : 'warning.main'}>{pct(school.fee_collection_rate)}</Typography>
                            </Box>
                            <LinearProgress variant="determinate" value={Math.min(parseFloat(school.fee_collection_rate) || 0, 100)}
                              color={school.fee_collection_rate >= 80 ? 'success' : 'warning'}
                              sx={{ height: 5, borderRadius: 3 }} />
                          </Box>

                          <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                            <Typography fontSize={10} color="text.secondary">
                              {school.academic_year_label} {school.snap_term}
                            </Typography>
                            <Typography fontSize={10} color="text.secondary">
                              Synced {fmtD(school.snapshot_date)}
                            </Typography>
                          </Stack>
                          {school.outstanding_fees > 0 && (
                            <Typography fontSize={10} color="error.main" sx={{ mt: 0.3 }}>
                              Outstanding: {fmt(school.outstanding_fees)}
                            </Typography>
                          )}
                        </>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Comparison table */}
          {data.schools?.some(s => s.sync_status === 'ok') && (
            <Card variant="outlined" sx={{ borderRadius: 2, mt: 3 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography fontWeight={700}>Network Comparison Table</Typography>
                  <Button size="small" variant="outlined" startIcon={<FileDownload />} onClick={() =>
                    exportCSV('network_comparison',
                      ['School', 'Type', 'Enrolled', 'Male', 'Female', 'Teachers', 'S:T Ratio', 'Avg Score', 'Attendance', 'Fee Rate', 'Outstanding'],
                      data.schools.filter(s => s.sync_status === 'ok').map(s => [
                        s.name, s.school_type, s.total_enrolled, s.male_enrolled, s.female_enrolled,
                        s.total_teachers, s.student_teacher_ratio,
                        `${s.avg_academic_score}%`, `${s.attendance_rate}%`, `${s.fee_collection_rate}%`,
                        s.outstanding_fees
                      ]))
                  }>Export</Button>
                </Stack>
                <TableContainer>
                  <Table size="small">
                    <TableHead><TableRow sx={{ bgcolor: '#0f2b4a' }}>
                      {['School', 'Enrolled', 'Staff', 'Ratio', 'Avg Score', 'Attend.', 'Fee Rate', 'Outstanding'].map(h => (
                        <TableCell key={h} sx={{ color: 'white', fontWeight: 700, fontSize: 11 }}>{h}</TableCell>
                      ))}
                    </TableRow></TableHead>
                    <TableBody>
                      {data.schools.filter(s => s.sync_status === 'ok' && s.total_enrolled != null)
                        .sort((a, b) => (b.total_enrolled || 0) - (a.total_enrolled || 0))
                        .map((s, i) => (
                        <TableRow key={s.id} hover sx={{ bgcolor: i % 2 === 0 ? 'white' : '#fafafa' }}>
                          <TableCell>
                            <Typography fontSize={12} fontWeight={600}>{s.name}</Typography>
                            <Chip label={s.code} size="small" sx={{ fontSize: 9, fontFamily: 'monospace' }} />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{fmtN(s.total_enrolled)}<Typography component="span" fontSize={10} color="text.secondary" sx={{ ml: 0.5 }}>{s.male_enrolled}M/{s.female_enrolled}F</Typography></TableCell>
                          <TableCell>{fmtN(s.total_teachers)}</TableCell>
                          <TableCell>{s.student_teacher_ratio}:1</TableCell>
                          <TableCell><Chip label={pct(s.avg_academic_score)} size="small" color={s.avg_academic_score >= 60 ? 'success' : 'warning'} sx={{ fontSize: 10, fontWeight: 700 }} /></TableCell>
                          <TableCell><Chip label={pct(s.attendance_rate)} size="small" color={s.attendance_rate >= 85 ? 'success' : 'warning'} sx={{ fontSize: 10, fontWeight: 700 }} /></TableCell>
                          <TableCell><Chip label={pct(s.fee_collection_rate)} size="small" color={s.fee_collection_rate >= 80 ? 'success' : 'error'} sx={{ fontSize: 10, fontWeight: 700 }} /></TableCell>
                          <TableCell sx={{ color: 'error.main', fontWeight: 700, fontSize: 12 }}>{fmt(s.outstanding_fees)}</TableCell>
                        </TableRow>
                      ))}
                      {/* Totals row */}
                      <TableRow sx={{ bgcolor: '#e8f0fe' }}>
                        <TableCell sx={{ fontWeight: 800 }}>NETWORK TOTAL</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>{fmtN(t.enrolled)}</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>{fmtN(t.teachers)}</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>{t.ratio}:1</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>{pct(t.avg_academic)}</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>{pct(t.avg_attendance)}</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>{pct(t.avg_fee_rate)}</TableCell>
                        <TableCell sx={{ fontWeight: 800, color: 'error.main' }}>{fmt(t.outstanding)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
}

// ── Schools Management Tab ─────────────────────────────────────────────────────
function SchoolsTab() {
  const { enqueueSnackbar } = useSnackbar();
  const [schools, setSchools]     = useState([]);
  const [networks, setNetworks]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [dialog, setDialog]       = useState(false);
  const [editing, setEditing]     = useState(null);
  const [testing, setTesting]     = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showPw, setShowPw]       = useState(false);
  const [histDialog, setHistDialog] = useState(null);
  const [history, setHistory]     = useState([]);

  const BLANK = { network_id:'', name:'', code:'', school_type:'secondary', address:'', phone:'', email:'', region:'', country:'Eswatini', db_host:'localhost', db_port:3306, db_name:'school_management', db_user:'root', db_password:'', joined_date:'', notes:'' };
  const [form, setForm] = useState(BLANK);
  const setF = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sr, nr] = await Promise.all([api.get('/network/schools'), api.get('/network/networks')]);
      if (sr.data.success) setSchools(sr.data.data);
      if (nr.data.success) setNetworks(nr.data.data);
    } catch { enqueueSnackbar('Failed to load', { variant: 'error' }); }
    finally { setLoading(false); }
  }, [enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name || !form.code || !form.db_name) return enqueueSnackbar('Name, code and database name required', { variant: 'warning' });
    try {
      if (editing) { await api.put(`/network/schools/${editing.id}`, form); enqueueSnackbar('Updated', { variant: 'success' }); }
      else { await api.post('/network/schools', form); enqueueSnackbar('School added', { variant: 'success' }); }
      setDialog(false); setEditing(null); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  const testConnection = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await api.post('/network/test-connection', { db_host: form.db_host, db_port: form.db_port, db_name: form.db_name, db_user: form.db_user, db_password: form.db_password });
      setTestResult({ ok: true, msg: r.data.message });
    } catch (err) { setTestResult({ ok: false, msg: err.response?.data?.message || 'Connection failed' }); }
    finally { setTesting(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Remove this school from the network?')) return;
    try { await api.delete(`/network/schools/${id}`); enqueueSnackbar('Removed', { variant: 'success' }); load(); }
    catch { enqueueSnackbar('Error', { variant: 'error' }); }
  };

  const openHistory = async (school) => {
    setHistDialog(school);
    const r = await api.get(`/network/schools/${school.id}/history`);
    if (r.data.success) setHistory(r.data.data);
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
        <Chip label={`${schools.length} school(s)`} size="small" />
        <Chip label={`${schools.filter(s => s.sync_status === 'ok').length} synced`} size="small" color="success" />
        <Chip label={`${schools.filter(s => s.sync_status === 'error').length} errors`} size="small" color="error" />
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="contained" startIcon={<Add />} onClick={() => { setEditing(null); setForm({...BLANK, network_id: networks[0]?.id || ''}); setTestResult(null); setDialog(true); }} sx={{ bgcolor: '#0f2b4a' }}>
          Add School
        </Button>
      </Stack>

      {loading ? <CircularProgress /> : (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow>
                {['School', 'Code', 'Type', 'Network', 'Database', 'Last Sync', 'Status', 'Actions'].map(h => (
                  <TableCell key={h} sx={{ bgcolor: '#0f2b4a', color: 'white', fontWeight: 700, fontSize: 11 }}>{h}</TableCell>
                ))}
              </TableRow></TableHead>
              <TableBody>
                {schools.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No schools in network yet. Add your first school.
                </TableCell></TableRow>}
                {schools.map((s, i) => (
                  <TableRow key={s.id} hover sx={{ bgcolor: s.sync_status === 'error' ? '#fff8f8' : i % 2 === 0 ? 'white' : '#fafafa' }}>
                    <TableCell>
                      <Typography fontSize={13} fontWeight={600}>{s.name}</Typography>
                      <Typography fontSize={10} color="text.secondary">{s.region} · {s.country}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 12 }}>{s.code}</TableCell>
                    <TableCell><Chip label={ptLabel(s.school_type)} size="small" sx={{ fontSize: 10 }} /></TableCell>
                    <TableCell sx={{ fontSize: 11 }}>{s.network_name}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{s.db_name}</TableCell>
                    <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>{fmtD(s.snapshot_date)}</TableCell>
                    <TableCell>
                      <Chip label={s.sync_status || 'Never synced'} size="small"
                        color={SYNC_COLOR[s.sync_status] || 'default'} sx={{ fontSize: 10, fontWeight: 700 }} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.3}>
                        <Tooltip title="Sync now">
                          <IconButton size="small" color="primary" onClick={async () => {
                            try { const r = await api.post(`/network/schools/${s.id}/sync`); if (r.data.success) { enqueueSnackbar('Synced', { variant: 'success' }); load(); } else enqueueSnackbar(r.data.message, { variant: 'warning' }); }
                            catch { enqueueSnackbar('Sync error', { variant: 'error' }); }
                          }}><Sync fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Sync history">
                          <IconButton size="small" onClick={() => openHistory(s)}><History fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => { setEditing(s); setForm({ network_id: s.network_id, name: s.name, code: s.code, school_type: s.school_type, address: s.address||'', phone: s.phone||'', email: s.email||'', region: s.region||'', country: s.country||'Eswatini', db_host: s.db_host||'localhost', db_port: s.db_port||3306, db_name: s.db_name, db_user: s.db_user||'root', db_password: '', joined_date: s.joined_date?.split('T')[0]||'', notes: s.notes||'' }); setTestResult(null); setDialog(true); }}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove from network">
                          <IconButton size="small" color="error" onClick={() => del(s.id)}><Delete fontSize="small" /></IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* School dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#0f2b4a', color: 'white' }}>{editing ? 'Edit School' : 'Add School to Network'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}><Typography variant="caption" fontWeight={700} color="primary" display="block">SCHOOL DETAILS</Typography></Grid>
            <Grid item xs={8}><TextField fullWidth size="small" label="School Name *" value={form.name} onChange={setF('name')} /></Grid>
            <Grid item xs={4}><TextField fullWidth size="small" label="Code *" value={form.code} onChange={setF('code')} placeholder="e.g. SHS, MBS" /></Grid>
            <Grid item xs={6} sm={4}>
              <TextField select fullWidth size="small" label="Type" value={form.school_type} onChange={setF('school_type')}>
                {SCHOOL_TYPES.map(t => <MenuItem key={t} value={t}>{ptLabel(t)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField select fullWidth size="small" label="Network *" value={form.network_id} onChange={setF('network_id')}>
                {networks.map(n => <MenuItem key={n.id} value={n.id}>{n.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Region / District" value={form.region} onChange={setF('region')} /></Grid>
            <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Country" value={form.country} onChange={setF('country')} /></Grid>
            <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Phone" value={form.phone} onChange={setF('phone')} /></Grid>
            <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Email" value={form.email} onChange={setF('email')} /></Grid>
            <Grid item xs={6} sm={4}><TextField fullWidth size="small" type="date" label="Joined Date" value={form.joined_date} onChange={setF('joined_date')} InputLabelProps={{ shrink: true }} /></Grid>

            <Grid item xs={12}><Divider /><Typography variant="caption" fontWeight={700} color="primary" display="block" sx={{ mt: 1 }}>DATABASE CONNECTION</Typography></Grid>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ fontSize: 12 }}>
                Each school runs its own database. Enter the connection details for this school's database. The network system will pull data from it during sync.
              </Alert>
            </Grid>
            <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="DB Host *" value={form.db_host} onChange={setF('db_host')} placeholder="localhost or IP" /></Grid>
            <Grid item xs={6} sm={2}><TextField fullWidth size="small" type="number" label="Port" value={form.db_port} onChange={setF('db_port')} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Database Name *" value={form.db_name} onChange={setF('db_name')} placeholder="school_management" /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="DB Username" value={form.db_user} onChange={setF('db_user')} /></Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label={editing ? "DB Password (leave blank to keep)" : "DB Password"} value={form.db_password} onChange={setF('db_password')}
                type={showPw ? 'text' : 'password'}
                InputProps={{ endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => setShowPw(v => !v)}>{showPw ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</IconButton></InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button variant="outlined" size="small" startIcon={testing ? <CircularProgress size={14} /> : <Cable />} onClick={testConnection} disabled={testing || !form.db_name}>
                  {testing ? 'Testing…' : 'Test Connection'}
                </Button>
                {testResult && (
                  <Alert severity={testResult.ok ? 'success' : 'error'} sx={{ flexGrow: 1, fontSize: 11, py: 0.5 }}>
                    {testResult.msg}
                  </Alert>
                )}
              </Stack>
            </Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Notes" value={form.notes} onChange={setF('notes')} multiline rows={2} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} sx={{ bgcolor: '#0f2b4a' }}>{editing ? 'Update' : 'Add School'}</Button>
        </DialogActions>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!histDialog} onClose={() => setHistDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Sync History — {histDialog?.name}</DialogTitle>
        <DialogContent dividers>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: '#f5f6fa' }}>
                {['Sync Date', 'Year/Term', 'Enrolled', 'Teachers', 'Avg Score', 'Attendance', 'Fee Rate', 'Status'].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11 }}>{h}</TableCell>
                ))}
              </TableRow></TableHead>
              <TableBody>
                {history.map((h, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ fontSize: 11 }}>{fmtD(h.snapshot_date)}</TableCell>
                    <TableCell sx={{ fontSize: 11 }}>{h.academic_year_label} {h.term}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{h.total_enrolled}</TableCell>
                    <TableCell>{h.total_teachers}</TableCell>
                    <TableCell><Chip label={pct(h.avg_academic_score)} size="small" color={h.avg_academic_score >= 60 ? 'success' : 'warning'} sx={{ fontSize: 10 }} /></TableCell>
                    <TableCell><Chip label={pct(h.attendance_rate)} size="small" color={h.attendance_rate >= 85 ? 'success' : 'warning'} sx={{ fontSize: 10 }} /></TableCell>
                    <TableCell><Chip label={pct(h.fee_collection_rate)} size="small" color={h.fee_collection_rate >= 80 ? 'success' : 'error'} sx={{ fontSize: 10 }} /></TableCell>
                    <TableCell><Chip label={h.sync_status} size="small" color={SYNC_COLOR[h.sync_status]} sx={{ fontSize: 10 }} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions><Button onClick={() => setHistDialog(null)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Network Users Tab ──────────────────────────────────────────────────────────
function UsersTab() {
  const { enqueueSnackbar } = useSnackbar();
  const [users, setUsers]     = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [showPw, setShowPw]   = useState(false);
  const BLANK = { username:'', password:'', full_name:'', email:'', role:'network_viewer', school_access:[], is_active:1 };
  const [form, setForm] = useState(BLANK);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ur, sr] = await Promise.all([api.get('/network/users'), api.get('/network/schools')]);
      if (ur.data.success) setUsers(ur.data.data);
      if (sr.data.success) setSchools(sr.data.data);
    } catch { enqueueSnackbar('Failed to load', { variant: 'error' }); }
    finally { setLoading(false); }
  }, [enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (editing) { await api.put(`/network/users/${editing.id}`, form); enqueueSnackbar('Updated', { variant: 'success' }); }
      else { await api.post('/network/users', form); enqueueSnackbar('User created', { variant: 'success' }); }
      setDialog(false); setEditing(null); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} justifyContent="flex-end">
        <Button variant="contained" startIcon={<Add />} onClick={() => { setEditing(null); setForm({...BLANK}); setDialog(true); }} sx={{ bgcolor: '#0f2b4a' }}>
          Add Network User
        </Button>
      </Stack>

      {loading ? <CircularProgress /> : (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow>
                {['Name', 'Username', 'Role', 'School Access', 'Status', 'Last Login', 'Actions'].map(h => (
                  <TableCell key={h} sx={{ bgcolor: '#0f2b4a', color: 'white', fontWeight: 700, fontSize: 11 }}>{h}</TableCell>
                ))}
              </TableRow></TableHead>
              <TableBody>
                {users.map((u, i) => {
                  let access = null;
                  try { access = JSON.parse(u.school_access || 'null'); } catch {}
                  return (
                    <TableRow key={u.id} hover sx={{ bgcolor: i % 2 === 0 ? 'white' : '#fafafa' }}>
                      <TableCell sx={{ fontWeight: 600 }}>{u.full_name}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{u.username}</TableCell>
                      <TableCell><Chip label={ptLabel(u.role)} size="small" color={u.role === 'network_admin' ? 'error' : 'default'} sx={{ fontSize: 10 }} /></TableCell>
                      <TableCell sx={{ fontSize: 11 }}>{!access ? <Chip label="All schools" size="small" color="success" sx={{ fontSize: 10 }} /> : `${access.length} school(s)`}</TableCell>
                      <TableCell><Chip label={u.is_active ? 'Active' : 'Inactive'} size="small" color={u.is_active ? 'success' : 'default'} sx={{ fontSize: 10 }} /></TableCell>
                      <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>{fmtD(u.last_login)}</TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => { setEditing(u); setForm({ username: u.username, password: '', full_name: u.full_name, email: u.email||'', role: u.role, school_access: (() => { try { return JSON.parse(u.school_access||'null') || []; } catch { return []; } })(), is_active: u.is_active }); setDialog(true); }}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Network User' : 'Add Network User'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={8}><TextField fullWidth size="small" label="Full Name *" value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} /></Grid>
            <Grid item xs={4}>
              <TextField select fullWidth size="small" label="Role" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                <MenuItem value="network_admin">Network Admin</MenuItem>
                <MenuItem value="network_viewer">Network Viewer</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Username *" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label={editing ? "Password (leave blank to keep)" : "Password *"} value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                type={showPw ? 'text' : 'password'}
                InputProps={{ endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => setShowPw(v => !v)}>{showPw ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</IconButton></InputAdornment> }} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.5 }}>School Access (leave empty for all schools)</Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.5}>
                {schools.map(s => {
                  const has = form.school_access?.includes(s.id);
                  return (
                    <Chip key={s.id} label={s.name} size="small" clickable
                      color={has ? 'primary' : 'default'} variant={has ? 'filled' : 'outlined'}
                      onClick={() => setForm(f => ({
                        ...f,
                        school_access: has
                          ? f.school_access.filter(id => id !== s.id)
                          : [...(f.school_access || []), s.id]
                      }))} sx={{ fontSize: 11 }} />
                  );
                })}
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel control={<Switch size="small" checked={!!form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked ? 1 : 0 }))} />}
                label={<Typography fontSize={13}>Account active</Typography>} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} sx={{ bgcolor: '#0f2b4a' }}>{editing ? 'Update' : 'Create User'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function NetworkManagement() {
  const [tab, setTab] = useState(0);
  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountTree color="primary" /> School Network
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Group dashboard · Per-school KPIs · Live sync · Comparison table · Network users
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>
        <strong>How it works:</strong> Each school runs its own independent database with no data leakage between schools. This module connects to each school's database on demand and pulls a snapshot of key statistics. Data is cached in the <code>school_network</code> database for the group dashboard. Run <code>schema_network.sql</code> to create the network database, then add schools and click "Sync All" to pull live data.
      </Alert>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Tab icon={<BarChart fontSize="small" />}     label="Group Dashboard"  iconPosition="start" sx={{ fontSize: 12 }} />
        <Tab icon={<School fontSize="small" />}       label="Schools"          iconPosition="start" sx={{ fontSize: 12 }} />
        <Tab icon={<Person fontSize="small" />}       label="Network Users"    iconPosition="start" sx={{ fontSize: 12 }} />
      </Tabs>

      <TabPanel value={tab} index={0}><GroupDashboard /></TabPanel>
      <TabPanel value={tab} index={1}><SchoolsTab /></TabPanel>
      <TabPanel value={tab} index={2}><UsersTab /></TabPanel>
    </Box>
  );
}
